import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Lightbulb,
  ShieldAlert,
  Sparkles,
  SkipForward,
  XCircle,
} from 'lucide-react';
import type { ImportSessionSummary } from '@/api/import-history';
import { formatDateTime, STEP_LABELS, STEP_ORDER } from '@/lib/import-history-labels';

/** Habillage du statut global du rapport. */
const OVERALL_META = {
  on_track: {
    label: 'Import réussi',
    icon: CheckCircle2,
    banner: 'border-green-200 bg-green-50 text-green-900',
    iconClass: 'text-green-600',
  },
  attention: {
    label: 'Import à vérifier',
    icon: AlertTriangle,
    banner: 'border-orange-200 bg-orange-50 text-orange-900',
    iconClass: 'text-orange-600',
  },
  critical: {
    label: 'Import en difficulté',
    icon: ShieldAlert,
    banner: 'border-red-200 bg-red-50 text-red-900',
    iconClass: 'text-red-600',
  },
} as const;

const SEVERITY_META = {
  info: { icon: Info, className: 'text-blue-600' },
  warning: { icon: AlertTriangle, className: 'text-orange-600' },
  critical: { icon: XCircle, className: 'text-red-600' },
} as const;

interface ImportReportViewProps {
  session: ImportSessionSummary;
  schoolName?: string;
}

/**
 * Rapport complet d'un import : synthèse rédigée (IA ou repli), détail par
 * type de données, échecs à corriger, ignorés et recommandations. Conçu pour
 * être lisible par tout le monde ET propre à l'impression (export PDF via la
 * boîte de dialogue d'impression du navigateur).
 */
export function ImportReportView({ session, schoolName }: ImportReportViewProps) {
  const analysis = session.aiAnalysis;
  const overall = OVERALL_META[analysis?.overallStatus ?? 'on_track'];
  const OverallIcon = overall.icon;
  const imported = session.createdCount + session.updatedCount;

  const analysisByStep = new Map(
    (analysis?.stepAnalyses ?? []).map((s) => [s.stepId, s] as const),
  );
  const presentSteps = STEP_ORDER.filter((s) => session.stepsSummary?.[s]).concat(
    Object.keys(session.stepsSummary ?? {}).filter((s) => !STEP_ORDER.includes(s)),
  );

  return (
    <div className="space-y-6 print:space-y-4 print:text-[13px]">
      {/* En-tête d'impression (masqué à l'écran, la page a son propre header) */}
      <div className="hidden print:block">
        <p className="text-xs text-gray-500">École Futée — Rapport d'import de données</p>
        <h1 className="text-xl font-bold text-black">
          {schoolName ?? ''} — import du {formatDateTime(session.startedAt)}
        </h1>
        <p className="text-xs text-gray-500">
          Réalisé par {session.createdBy || 'Inconnu'}
          {session.targetSchoolPeriodLabel ? ` · ${session.targetSchoolPeriodLabel}` : ''}
        </p>
      </div>

      {/* Statut global + synthèse rédigée */}
      <div className={`rounded-xl border p-5 print:rounded-none print:p-3 ${overall.banner}`}>
        <div className="flex items-center gap-2">
          <OverallIcon className={`h-5 w-5 ${overall.iconClass}`} />
          <p className="text-base font-semibold">{overall.label}</p>
        </div>
        {analysis?.executiveSummary && (
          <p className="mt-2 text-sm leading-relaxed">{analysis.executiveSummary}</p>
        )}
      </div>

      {/* Chiffres clés */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <ReportStat
          label="Données importées"
          value={imported}
          icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
        />
        <ReportStat
          label="Déjà présentes (ignorées)"
          value={session.skippedCount}
          icon={<SkipForward className="h-4 w-4 text-muted-foreground" />}
        />
        <ReportStat
          label="En échec"
          value={session.failedCount}
          icon={<XCircle className="h-4 w-4 text-red-600" />}
        />
        <ReportStat
          label="Total traité"
          value={session.totalItems}
          icon={<Info className="h-4 w-4 text-blue-600" />}
        />
      </div>

      {/* Détail par type de données */}
      {presentSteps.length > 0 && (
        <Card className="print:border-gray-300 print:shadow-none">
          <CardHeader>
            <CardTitle className="text-base">Détail par type de données</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {presentSteps.map((stepId) => {
              const step = session.stepsSummary?.[stepId];
              if (!step) return null;
              const ok = step.created + step.updated + step.skipped;
              const percent = step.total > 0 ? Math.round((ok / step.total) * 100) : 100;
              const stepAnalysis = analysisByStep.get(stepId);
              return (
                <div key={stepId} className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="min-w-40 text-sm font-medium">
                      {STEP_LABELS[stepId] ?? stepId}
                    </p>
                    <div className="flex flex-1 items-center gap-3">
                      <Progress value={percent} className="h-2 flex-1" />
                      <span className="w-10 text-right text-xs text-muted-foreground">
                        {percent}%
                      </span>
                    </div>
                    <div className="flex gap-1.5 text-xs">
                      {step.created > 0 && (
                        <Badge variant="outline" className="border-0 bg-green-100 text-green-700">
                          {step.created.toLocaleString('fr-FR')} créé{step.created > 1 ? 's' : ''}
                        </Badge>
                      )}
                      {step.updated > 0 && (
                        <Badge variant="outline" className="border-0 bg-blue-100 text-blue-700">
                          {step.updated.toLocaleString('fr-FR')} mis à jour
                        </Badge>
                      )}
                      {step.skipped > 0 && (
                        <Badge variant="outline" className="border-0 bg-gray-100 text-gray-600">
                          {step.skipped.toLocaleString('fr-FR')} ignoré{step.skipped > 1 ? 's' : ''}
                        </Badge>
                      )}
                      {step.failed > 0 && (
                        <Badge variant="outline" className="border-0 bg-red-100 text-red-700">
                          {step.failed.toLocaleString('fr-FR')} échec{step.failed > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {stepAnalysis?.analysis && (
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {stepAnalysis.analysis}
                    </p>
                  )}
                  {(stepAnalysis?.alerts ?? []).map((alert, i) => (
                    <p key={i} className="flex gap-1.5 text-sm text-orange-700">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {alert}
                    </p>
                  ))}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Ce qui n'a pas été importé */}
      {(analysis?.failedHighlights?.length ?? 0) > 0 && (
        <Card className="print:border-gray-300 print:shadow-none">
          <CardHeader icon={<XCircle />} iconColor="red">
            <CardTitle className="text-base">Ce qui n'a pas été importé</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {analysis!.failedHighlights.map((highlight, i) => {
              const meta = SEVERITY_META[highlight.severity] ?? SEVERITY_META.warning;
              const SeverityIcon = meta.icon;
              return (
                <div key={i} className="flex gap-2.5">
                  <SeverityIcon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.className}`} />
                  <div>
                    <p className="text-sm font-medium">{highlight.scope}</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {highlight.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Éléments ignorés */}
      {analysis?.skippedSummary && (
        <Card className="print:border-gray-300 print:shadow-none">
          <CardHeader icon={<SkipForward />} iconColor="blue">
            <CardTitle className="text-base">Éléments ignorés (déjà présents)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {analysis.skippedSummary}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recommandations */}
      {(analysis?.recommendations?.length ?? 0) > 0 && (
        <Card className="print:border-gray-300 print:shadow-none">
          <CardHeader icon={<Lightbulb />} iconColor="orange">
            <CardTitle className="text-base">Recommandations</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-2 pl-5">
              {analysis!.recommendations.map((rec, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  {rec}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Conclusion */}
      {analysis?.conclusion && (
        <Card className="print:border-gray-300 print:shadow-none">
          <CardContent className="pt-6">
            <p className="text-sm font-medium leading-relaxed">{analysis.conclusion}</p>
          </CardContent>
        </Card>
      )}

      {/* Pied de rapport */}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {session.aiGenerated && <Sparkles className="h-3.5 w-3.5" />}
        {session.aiGenerated
          ? 'Analyse rédigée par intelligence artificielle à partir des chiffres exacts de l’import'
          : 'Synthèse automatique (analyse IA indisponible) — chiffres exacts de l’import'}
        {session.reportGeneratedAt && <> · rapport généré le {formatDateTime(session.reportGeneratedAt)}</>}
      </p>
    </div>
  );
}

function ReportStat({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <Card className="print:border-gray-300 print:shadow-none">
      <CardContent className="flex items-center gap-3 py-4">
        {icon}
        <div>
          <p className="text-lg font-semibold leading-tight">{value.toLocaleString('fr-FR')}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
